import type { DocAsset } from "@/lib/document/assetTypes";
import type { DocBlock } from "@/lib/document-builder/blocks/blockTypes";
import {
  addTableColumn,
  addTableRow,
  removeTableColumn,
  removeTableRow,
  updateTableCell,
} from "@/lib/document-builder/blocks/blockTableHelpers";
import { DocumentTwoColumnFields } from "./DocumentTwoColumnFields";

type Props = {
  block: DocBlock;
  assets: DocAsset[];
  onChange: (block: DocBlock) => void;
};

export function DocumentAdvancedBlockFields({ block, assets, onChange }: Props) {
  switch (block.type) {
    case "number_field":
      return (
        <div className="space-y-2">
          <label className="block text-xs">
            <span className="text-gray-500">Label</span>
            <input
              type="text"
              value={block.label}
              onChange={(e) => onChange({ ...block, label: e.target.value })}
              className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-800"
            />
          </label>
          <label className="block text-xs">
            <span className="text-gray-500">Variable key</span>
            <input
              type="text"
              value={block.variable_key}
              onChange={(e) => onChange({ ...block, variable_key: e.target.value })}
              placeholder="Price"
              className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 font-mono text-sm dark:border-zinc-700 dark:bg-zinc-800"
            />
          </label>
        </div>
      );
    case "checkbox":
      return (
        <div className="space-y-2">
          <label className="block text-xs">
            <span className="text-gray-500">Label</span>
            <input
              type="text"
              value={block.label}
              onChange={(e) => onChange({ ...block, label: e.target.value })}
              className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-800"
            />
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={block.fillable}
              onChange={(e) =>
                onChange({
                  ...block,
                  fillable: e.target.checked,
                  variable_key: e.target.checked ? block.variable_key : "",
                })
              }
            />
            <span className="text-gray-600 dark:text-zinc-300">Fillable at export (variable)</span>
          </label>
          {block.fillable ? (
            <label className="block text-xs">
              <span className="text-gray-500">Variable key</span>
              <input
                type="text"
                value={block.variable_key}
                onChange={(e) => onChange({ ...block, variable_key: e.target.value })}
                placeholder="Agreed"
                className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 font-mono text-sm dark:border-zinc-700 dark:bg-zinc-800"
              />
            </label>
          ) : (
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={block.checked}
                onChange={(e) => onChange({ ...block, checked: e.target.checked })}
              />
              <span className="text-gray-600 dark:text-zinc-300">Checked (static)</span>
            </label>
          )}
        </div>
      );
    case "signature_line":
      return (
        <div className="space-y-2">
          <label className="block text-xs">
            <span className="text-gray-500">Printed name label</span>
            <input
              type="text"
              value={block.printed_name_label}
              onChange={(e) => onChange({ ...block, printed_name_label: e.target.value })}
              className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-800"
            />
          </label>
          <label className="block text-xs">
            <span className="text-gray-500">Name variable key</span>
            <input
              type="text"
              value={block.printed_name_variable_key}
              onChange={(e) => onChange({ ...block, printed_name_variable_key: e.target.value })}
              placeholder="CustomerName"
              className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 font-mono text-sm dark:border-zinc-700 dark:bg-zinc-800"
            />
          </label>
          <label className="block text-xs">
            <span className="text-gray-500">Signature caption</span>
            <input
              type="text"
              value={block.signature_caption}
              onChange={(e) => onChange({ ...block, signature_caption: e.target.value })}
              className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-800"
            />
          </label>
          <label className="block text-xs">
            <span className="text-gray-500">Signature variable key</span>
            <input
              type="text"
              value={block.signature_variable_key}
              onChange={(e) => onChange({ ...block, signature_variable_key: e.target.value })}
              placeholder="CustomerSignature"
              className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 font-mono text-sm dark:border-zinc-700 dark:bg-zinc-800"
            />
          </label>
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="block text-xs">
              <span className="text-gray-500">Date label</span>
              <input
                type="text"
                value={block.date_label}
                onChange={(e) => onChange({ ...block, date_label: e.target.value })}
                placeholder="Date"
                className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-800"
              />
            </label>
            <label className="block text-xs">
              <span className="text-gray-500">Date variable key</span>
              <input
                type="text"
                value={block.date_variable_key}
                onChange={(e) => onChange({ ...block, date_variable_key: e.target.value })}
                placeholder="SignedDate"
                className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 font-mono text-sm dark:border-zinc-700 dark:bg-zinc-800"
              />
            </label>
          </div>
          <p className="text-[10px] text-gray-500 dark:text-zinc-400">
            Signature and date render side-by-side on the PDF with fill lines for export-time entry.
          </p>
        </div>
      );
    case "table":
      return (
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={block.header_row}
              onChange={(e) => onChange({ ...block, header_row: e.target.checked })}
            />
            <span className="text-gray-600 dark:text-zinc-300">First row is header</span>
          </label>
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-xs">
              <tbody>
                {block.rows.map((row, ri) => (
                  <tr key={ri}>
                    {row.map((cell, ci) => (
                      <td key={ci} className="border border-gray-200 p-1 dark:border-zinc-700">
                        <input
                          type="text"
                          value={cell}
                          onChange={(e) =>
                            onChange(updateTableCell(block, ri, ci, e.target.value))
                          }
                          className={[
                            "w-full min-w-[80px] rounded px-1 py-0.5 dark:bg-zinc-800",
                            block.header_row && ri === 0 ? "font-semibold" : "",
                          ].join(" ")}
                        />
                      </td>
                    ))}
                    <td className="border-0 p-1">
                      <button
                        type="button"
                        onClick={() => onChange(removeTableRow(block, ri))}
                        className="text-[10px] text-red-600"
                      >
                        −row
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => onChange(addTableRow(block))}
              className="rounded border border-gray-300 px-2 py-0.5 text-[10px] dark:border-zinc-600"
            >
              Add row
            </button>
            <button
              type="button"
              onClick={() => onChange(addTableColumn(block))}
              className="rounded border border-gray-300 px-2 py-0.5 text-[10px] dark:border-zinc-600"
            >
              Add column
            </button>
            {(block.rows[0]?.length ?? 0) > 1 ? (
              <button
                type="button"
                onClick={() =>
                  onChange(removeTableColumn(block, (block.rows[0]?.length ?? 1) - 1))
                }
                className="rounded border border-gray-300 px-2 py-0.5 text-[10px] dark:border-zinc-600"
              >
                Remove last column
              </button>
            ) : null}
          </div>
        </div>
      );
    case "two_column":
      return (
        <DocumentTwoColumnFields
          block={block}
          assets={assets}
          onChange={onChange}
        />
      );
    case "component_ref":
      return (
        <p className="text-sm text-amber-700 dark:text-amber-300">
          Component reference blocks are reserved for a future release and cannot be edited in V1.
        </p>
      );
    default:
      return null;
  }
}
